<template>
  <div v-if="recipient">
    <b-navbar class="main_nav px-5 py-3 justify-content-between" data-cy="musashi-navbar">
      <b-navbar-brand to="/app">
        <UELogo />
      </b-navbar-brand>
      <b-button to="/app" variant="outline-primary" v-if="user">Return to the App</b-button>
    </b-navbar>
    <div class="container mt-5">
      <div class="d-block respondent-details respondent-details--fullpage">
        <h2>{{ displayName(recipient.first_name, recipient.last_name) }}</h2>
        <div class="text-muted mt-1">{{ recipient.email }}</div>
        <section class="recipient mt-3">
          <div>
            <table class="recipient-details">
              <tr>
                <td class="text-nowrap pr-5 text-muted">Title</td>
                <td>{{ recipient.title }}</td>
              </tr>
              <tr>
                <td class="text-nowrap pr-5 text-muted">Company</td>
                <td>{{ recipient.best_company_name }}</td>
              </tr>
              <tr v-if="survey">
                <td class="text-nowrap pr-5 text-muted">Survey</td>
                <td>{{ survey.name }}</td>
              </tr>
              <tr>
                <td class="text-nowrap pr-5 text-muted">Response</td>
                <td>{{ responseType }}</td>
              </tr>
              <tr>
                <td class="text-nowrap pr-5 text-muted">Date</td>
                <td>
                  {{ new Date(recipient.last_response_at).toLocaleString() }}
                </td>
              </tr>
              <tr>
                <td class="text-nowrap pr-5 text-muted">Name Usage</td>
                <td>
                  {{ recipient.named ? 'Approved' : 'Not approved' }}
                </td>
              </tr>
              <tr>
                <td class="text-nowrap pr-5 text-muted">Advocate Activities</td>
                <td>
                  <b-badge
                    v-for="activity in volunteer_activities"
                    :key="activity"
                    :style="{ 'background-color': pillColor(activity) }"
                  >
                    {{ volunteer_map[activity] ? volunteer_map[activity].short_title : activity }}
                  </b-badge>
                </td>
              </tr>
            </table>
          </div>
        </section>
        <section>
          <div class="questions">
            <div v-for="(question, i) in questions" :key="i">
              <div class="question_container d-flex">
                <div class="the_number">
                  <div>{{ i + 1 }}</div>
                </div>
                <div class="the_question">
                  <div v-if="['MultipleChoiceMany', 'ReferenceVolunteer'].includes(question.type)">
                    <MultipleChoiceMany
                      :question="question"
                      :recipient_id="recipientId"
                      :show_all="true"
                    ></MultipleChoiceMany>
                  </div>
                  <div
                    v-else-if="['MultipleChoiceOne', 'NamedAttribution'].includes(question.type)"
                  >
                    <MultipleChoiceOne
                      :question="question"
                      :recipient_id="recipientId"
                      :show_all="true"
                    ></MultipleChoiceOne>
                  </div>
                  <div v-else-if="question.type == 'Nps'">
                    <Nps :question="question" :recipient_id="recipientId"></Nps>
                  </div>
                  <div v-else-if="question.type == 'Testimonial'">
                    <Testimonial :question="question" :recipient_id="recipientId"></Testimonial>
                  </div>
                  <div v-else-if="question.type == 'Slider'">
                    <Slider :question="question" :recipient_id="recipientId"></Slider>
                  </div>
                  <div v-else-if="question.type == 'Matrixx'">
                    <Matrixx :question="question" :recipient_id="recipientId.toString()"></Matrixx>
                  </div>
                  <div v-else-if="question.type == 'ClassifierMcoQuestion'">
                    <MultipleChoiceOne
                      :question="question"
                      :recipient_id="recipientId"
                      :show_all="true"
                    ></MultipleChoiceOne>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
// This is a special request from the business. It's a publicly exposed page that
// we'll push certain customers via the workato
// api where they can see a recipients survey responses.
import { ref, computed } from 'vue'
import { useQuestionStore } from '@app/stores/QuestionStore.ts'
import { useRecipientStore } from '@app/stores/RecipientStore.ts'
import { useSurveyStore } from '@app/stores/SurveyStore.ts'
import { useAccountStore } from '@app/stores/AccountStore.ts'
import { getRecipientByToken } from '@/survey/api.js'
import { displayName } from '@lib/formatters'
import MultipleChoiceOne from '@app/components/customer_spotlight/questions/MultipleChoiceOne.vue'
import MultipleChoiceMany from '@app/components/customer_spotlight/questions/MultipleChoiceMany.vue'
import Nps from '@app/components/customer_spotlight/questions/Nps.vue'
import Testimonial from '@app/components/customer_spotlight/questions/Testimonial.vue'
import Slider from '@app/components/customer_spotlight/questions/Slider.vue'
import Matrixx from '@app/components/customer_spotlight/questions/Matrixx.vue'
import UELogo from '@components/graphics/UELogo.vue'

const props = defineProps(['token'])

// Computed
const recipients = computed(() => useRecipientStore().recipients)
const recipient = computed(() => recipients.value.find((r) => r.token === props.token))
const recipientId = computed(() => recipient.value.id)
const survey = computed(() => useSurveyStore().getSurvey(recipient.value.survey_id))
const questions = computed(() => useQuestionStore().questions)
const responseType = computed(() =>
  recipient.value.type == 'WebLinkRecipient' ? 'Web link' : 'Email invitation',
)

const volunteer_activities = ref(null)
const volunteer_map = ref(null)

const accountStore = useAccountStore()
accountStore.getApp()
const user = computed(() => accountStore.user)

getRecipientByToken(props.token).then((res) => {
  useRecipientStore().upsertRecipientAndResponses(res.data.recipient)
  useQuestionStore().upsertQuestionsAndAnswers(res.data.recipient.questions)
  useSurveyStore().upsertSurvey(res.data.recipient.survey)
  volunteer_activities.value = res.data.recipient.volunteer_activities
  volunteer_map.value = res.data.recipient.volunteer_map
})
function pillColor(volunteer_activity) {
  if (volunteer_map.value[volunteer_activity]) {
    return volunteer_map.value[volunteer_activity].color
  } else {
    return volunteer_map.value['custom'].color
  }
}
</script>
<style lang="sass">
.recipient-details
  line-height: 2 // Bootstrap has no utility classes for line-heights
  tr td .badge
    margin-right: 0.5rem
    padding: 4px 8px
    font-weight: 400
    color: black

.question_container
  overflow: hidden
  padding: 16px 16px 12px 0
  margin: 16px 16px 24px 0
.the_number
  position: relative
  min-width: 32px
  margin-right: 20px
  &:after
    position: absolute
    content: ''
    border-right: 2px solid #CED4DA
    height: 100%
    top: 50px
    right: 50%
  div
    background: var(--ueLighterPurple)
    border-radius: 50%
    font-size: 14px
    font-weight: 700
    width: 32px
    height: 32px
    display: table-cell
    vertical-align: middle
    text-align: center
</style>
